import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Button, Input, Form, Typography, Divider, Space,
  Tabs, Modal, Alert, Spin
} from 'antd';
import {
  WalletOutlined, MobileOutlined,
  QrcodeOutlined, ClockCircleOutlined, CheckCircleOutlined,
  CloseCircleOutlined, ArrowLeftOutlined,
  SafetyOutlined, HistoryOutlined, ExclamationCircleOutlined,
  LoadingOutlined, WarningOutlined, IdcardOutlined
} from '@ant-design/icons';
import { postPayURequest, getRequest } from '../../Utils/AppApi';
import { getUser } from '../../Utils/Common';
import { useHistory } from "react-router-dom";
import { toast } from 'react-toastify';
import FingPayUpiCwHistory from '../reports/upiAtmHistory';
import 'react-toastify/dist/ReactToastify.css';

import { FING_PAY_CHECK_EKYC_STATUS } from '../../Utils/appConstants';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const PRIMARY_COLOR = '#C9262C';
const PRIMARY_LIGHT = '#fef0f0';

const MIN_AMOUNT = 100;
const MAX_AMOUNT = 5000;
const AMOUNT_STEP = 100;

const DEFAULT_QR_EXPIRY_SECONDS = 60;
const DEFAULT_START_IN_SECONDS = 60;
const DEFAULT_GAP_SECONDS = 30;
const DEFAULT_MAX_ATTEMPTS = 5;

const FING_UPI_CW_GENERATE_QR = 'fing/upi-cw/generate-qr';
const FING_UPI_CW_CHECK_STATUS = 'fing/upi-cw/check-status';

const QR_STATE = {
  INPUT: 'input',
  GENERATING: 'generating',
  ACTIVE: 'active',
  PENDING_RETRY: 'pending',
  TERMINAL: 'terminal'
};

const KYC_STATE = {
  CHECKING: 'checking',
  VERIFIED: 'verified',
  PENDING:  'pending',
  ERROR:    'error'
};

const FingPayUpiCw = () => {
  const history = useHistory();
  const [form] = Form.useForm();

  const qrTimerRef = useRef(null);
  const retryTimerRef = useRef(null);
  const redirectedRef = useRef(false);
  const retryAttemptsRef = useRef(0);
  const maxAttemptsRef = useRef(DEFAULT_MAX_ATTEMPTS);
  const gapSecondsRef = useRef(DEFAULT_GAP_SECONDS);

  // --------- KYC gate ---------
  const [kycState, setKycState] = useState(KYC_STATE.CHECKING);

  // --------- UI ---------
  const [activeTab, setActiveTab] = useState('generate');
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");

  const [qrState, setQrState] = useState(QR_STATE.INPUT);
  const [formKey, setFormKey] = useState(0);

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(DEFAULT_QR_EXPIRY_SECONDS);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [amountInput, setAmountInput] = useState('');
  const [sessionSnapshot, setSessionSnapshot] = useState({ mobile: '', amount: '', partnerRequestId: '' });
  const [transactionAmount, setTransactionAmount] = useState(0);

  const [abandonModal, setAbandonModal] = useState(false);
  const [outsideWindow, setOutsideWindow] = useState(false);

  const quickAmounts = [500, 1000, 2000, 5000];

  // ============================================================
  // KYC CHECK — runs once on mount.
  // If not completed → redirect immediately to /partner-ekyc.
  // ============================================================
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const response = await getRequest(FING_PAY_CHECK_EKYC_STATUS, history);

        if (cancelled) return;

        const isCompleted =
          response &&
          response.success === true &&
          response.data &&
          (response.data.isCompleted === true || response.data.isCompleted === 'true');

        if (isCompleted) {
          setKycState(KYC_STATE.VERIFIED);
        } else {
          setKycState(KYC_STATE.PENDING);
          history.push({ pathname: '/partner-ekyc' });
        }
      } catch (err) {
        if (cancelled) return;
        setKycState(KYC_STATE.PENDING);
        history.push({ pathname: '/partner-ekyc' });
      }
    };

    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================
  // Time-window check (23:30 – 00:05)
  // ============================================================
  const checkTimeWindow = () => {
    const now = new Date();
    const hhmm = now.getHours() * 100 + now.getMinutes();
    const blocked = hhmm >= 2330 || hhmm < 5;
    setOutsideWindow(blocked);
    return blocked;
  };

  useEffect(() => {
    checkTimeWindow();
    const t = setInterval(checkTimeWindow, 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // ============================================================
  // Location
  // ============================================================
  useEffect(() => {
    const userData = getUser();
    const shopsLat = userData?.data?.shopsLat;
    const shopsLong = userData?.data?.shopsLong;
    if (shopsLat && Number(shopsLat) !== 0) {
      setLatitude(Number(shopsLat));
      setLongitude(Number(shopsLong));
    } else {
      navigator.geolocation.getCurrentPosition(
        (data) => { setLatitude(data.coords.latitude); setLongitude(data.coords.longitude); },
        () => { setLatitude("28.50937"); setLongitude("77.29727"); }
      );
    }
  }, []);

  // ============================================================
  // Cleanup
  // ============================================================
  const clearTimers = () => {
    if (qrTimerRef.current) { clearInterval(qrTimerRef.current); qrTimerRef.current = null; }
    if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
  };

  useEffect(() => () => clearTimers(), []);

  // ============================================================
  // Helpers
  // ============================================================
  const formatTime = (s) =>
    `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setAmountInput('');
      form.setFieldsValue({ amount: '' });
      return;
    }
    const num = parseFloat(value);
    if (isNaN(num)) return;
    if (num > MAX_AMOUNT) {
      setAmountInput(MAX_AMOUNT.toString());
      form.setFieldsValue({ amount: MAX_AMOUNT.toString() });
      toast.warning(`Maximum withdrawal amount is ₹${MAX_AMOUNT}`);
    } else {
      setAmountInput(value);
      form.setFieldsValue({ amount: value });
    }
  };

  const handleQuickAmount = (amount) => {
    if (qrState !== QR_STATE.INPUT) return;
    setAmountInput(amount.toString());
    form.setFieldsValue({ amount: amount.toString() });
  };

  const goToInvoice = (partnerRequestId) => {
    if (redirectedRef.current) return;
    redirectedRef.current = true;
    clearTimers();
    setQrState(QR_STATE.TERMINAL);
    history.push(`/upi-atm-invoice/${partnerRequestId}`);
  };

  const performFullReset = () => {
    clearTimers();
    redirectedRef.current = false;
    retryAttemptsRef.current = 0;
    maxAttemptsRef.current = DEFAULT_MAX_ATTEMPTS;
    gapSecondsRef.current = DEFAULT_GAP_SECONDS;

    setQrState(QR_STATE.INPUT);
    setCurrentStep(0);
    setQrData(null);
    setTimeLeft(DEFAULT_QR_EXPIRY_SECONDS);
    setAmountInput('');
    setSessionSnapshot({ mobile: '', amount: '', partnerRequestId: '' });
    setTransactionAmount(0);
    setAbandonModal(false);

    setFormKey(k => k + 1);
    form.resetFields();
  };

  // ============================================================
  // Generate QR
  // ============================================================
  const handleGenerateQR = async (values) => {
    if (kycState !== KYC_STATE.VERIFIED) {
      toast.error('KYC verification required. Redirecting…');
      return;
    }
    if (qrState !== QR_STATE.INPUT) {
      toast.warning('A QR code is already active.');
      return;
    }
    if (outsideWindow) {
      toast.error('UPI Cash Withdrawal is restricted between 23:30 and 00:05.');
      return;
    }

    const amountNum = parseFloat(values.amount);
    if (amountNum < MIN_AMOUNT || amountNum > MAX_AMOUNT) {
      toast.error(`Amount must be between ₹${MIN_AMOUNT} and ₹${MAX_AMOUNT}`);
      return;
    }
    if (amountNum % AMOUNT_STEP !== 0) {
      toast.error(`Amount must be a multiple of ₹${AMOUNT_STEP}`);
      return;
    }

    setQrState(QR_STATE.GENERATING);
    setLoading(true);

    try {
      const payload = {
        mobile: values.mobile,
        amount: values.amount.toString(),
        devicetype: 'W',
        latitude: latitude.toString() || "28.50937",
        longitude: longitude.toString() || "77.29727"
      };

      const res = await postPayURequest(FING_UPI_CW_GENERATE_QR, payload, history);

      if (res.success && res.data && res.data.intentLink) {
        redirectedRef.current = false;
        retryAttemptsRef.current = 0;

        maxAttemptsRef.current = res.data.poll?.maxAttempts || DEFAULT_MAX_ATTEMPTS;
        gapSecondsRef.current = res.data.poll?.intervalSeconds || DEFAULT_GAP_SECONDS;

        setQrData(res.data);
        setTransactionAmount(parseFloat(values.amount));
        setSessionSnapshot({
          mobile: values.mobile,
          amount: values.amount,
          partnerRequestId: res.data.partnerRequestId
        });
        setCurrentStep(1);

        const expirySec = res.data.qrExpiryIn || DEFAULT_QR_EXPIRY_SECONDS;
        setTimeLeft(expirySec);
        setQrState(QR_STATE.ACTIVE);

        startQrCountdown(expirySec, res.data.partnerRequestId);
        toast.success('QR Code generated successfully');
      } else {
        setQrState(QR_STATE.INPUT);
        toast.error(res.message || 'Failed to generate QR code');
      }
    } catch (err) {
      setQrState(QR_STATE.INPUT);
      toast.error(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // Countdown
  // ============================================================
  const startQrCountdown = (seconds, partnerRequestId) => {
    if (qrTimerRef.current) clearInterval(qrTimerRef.current);
    setTimeLeft(seconds);

    qrTimerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(qrTimerRef.current);
          qrTimerRef.current = null;
          beginStatusPolling(partnerRequestId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // ============================================================
  // Status polling
  // ============================================================
  const beginStatusPolling = (partnerRequestId) => {
    setQrState(QR_STATE.PENDING_RETRY);
    retryAttemptsRef.current = 0;
    runStatusCheckLoop(partnerRequestId);
  };

  const runStatusCheckLoop = async (partnerRequestId) => {
    if (redirectedRef.current) return;
    retryAttemptsRef.current += 1;

    if (retryAttemptsRef.current > maxAttemptsRef.current) {
      goToInvoice(partnerRequestId);
      return;
    }

    setCheckingStatus(true);
    let terminal = false;

    try {
      const res = await postPayURequest(FING_UPI_CW_CHECK_STATUS, {
        partnerRequestId,
        devicetype: 'W',
        latitude: latitude.toString() || "28.50937",
        longitude: longitude.toString() || "77.29727"
      }, history);

      if (res.success && res.data) {
        const st = res.data.status || res.data.qrStatus;
        if (st === 'SUCCESS' || st === 'FAILED' || st === 'EXPIRED') {
          terminal = true;
          goToInvoice(partnerRequestId);
        }
      }
    } catch (_) {
      // keep looping on network error
    } finally {
      setCheckingStatus(false);
    }

    if (terminal || redirectedRef.current) return;

    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      runStatusCheckLoop(partnerRequestId);
    }, gapSecondsRef.current * 1000);
  };

  // ============================================================
  // Derived
  // ============================================================
  const kycBlocking = kycState !== KYC_STATE.VERIFIED;
  const isFormLocked = qrState !== QR_STATE.INPUT || outsideWindow || kycBlocking;
  const showOverlay = qrState === QR_STATE.PENDING_RETRY || qrState === QR_STATE.TERMINAL;

  // ============================================================
  // KYC checking screen — the only KYC state the user ever sees
  // (PENDING and ERROR both redirect immediately)
  // ============================================================
  if (kycState === KYC_STATE.CHECKING) {
    return (
      <div className="content-wrapper pt-2" style={{ padding: 0, margin: 0 }}>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-md-12">
                <div className="card rounded-0" style={{ minHeight: 400 }}>
                  <div className="card-body" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 24px',
                    textAlign: 'center'
                  }}>
                    <Spin
                      indicator={<LoadingOutlined style={{ fontSize: 56, color: PRIMARY_COLOR }} spin />}
                    />
                    <Title level={4} style={{ marginTop: 24, marginBottom: 8 }}>
                      Verifying your KYC
                    </Title>
                    <Text style={{ color: '#888', fontSize: 14 }}>
                      Please wait while we check your KYC status…
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // Main render — reached only when KYC is VERIFIED
  // ============================================================
  return (
    <div className="content-wrapper pt-2" style={{ padding: 0, margin: 0 }}>
      <style>{`
        .fing-upi-tabs .ant-tabs-nav { margin-bottom: 20px !important; }
        .fing-upi-tabs .ant-tabs-nav::before { border-bottom: 2px solid #f0f0f0 !important; }
        .fing-upi-tabs .ant-tabs-tab {
          padding: 10px 20px !important; font-size: 14px; font-weight: 500;
          color: #666; border-radius: 6px 6px 0 0 !important;
          background: transparent !important; border: none !important;
        }
        .fing-upi-tabs .ant-tabs-tab:hover { color: ${PRIMARY_COLOR} !important; }
        .fing-upi-tabs .ant-tabs-tab-active {
          background: #fff !important; border: 2px solid ${PRIMARY_COLOR} !important;
          border-bottom-color: #fff !important; color: ${PRIMARY_COLOR} !important;
          font-weight: 600 !important; margin-bottom: -2px !important;
        }
        .fing-upi-tabs .ant-tabs-ink-bar { display: none !important; }
        .fing-upi-hero {
          background: #fff; border: 1px solid #e8e8e8; border-radius: 12px;
          padding: 20px; display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;
        }
        .fing-upi-hero h3 { color: #1a1a1a !important; }
        .fing-upi-hero .ant-typography { color: #666 !important; }
        .fing-upi-info {
          background: #fafafa; border: 1px solid #e8e8e8; border-radius: 10px;
          padding: 10px 14px; display: flex; align-items: center; margin-bottom: 16px;
        }
        .fing-upi-input { border-radius: 8px !important; border-color: #d9d9d9 !important; }
        .fing-upi-input:hover { border-color: ${PRIMARY_COLOR}60 !important; }
        .fing-upi-input:focus, .ant-input-focused { border-color: ${PRIMARY_COLOR} !important; box-shadow: 0 0 0 2px ${PRIMARY_COLOR}15 !important; }
        .fing-upi-quick { display: flex; gap: 8px; flex-wrap: wrap; }
        .fing-upi-quick-btn {
          border-radius: 18px; border: 1px solid #d9d9d9;
          color: #555; background: #fff; font-weight: 500;
        }
        .fing-upi-quick-btn:hover:not(:disabled) { background: ${PRIMARY_LIGHT}; border-color: ${PRIMARY_COLOR}40; color: ${PRIMARY_COLOR}; }
        .fing-upi-gen-btn {
          height: 48px !important; border-radius: 24px !important;
          background: ${PRIMARY_COLOR} !important; border: none !important;
          font-size: 15px !important; font-weight: 600 !important;
        }
        .fing-upi-gen-btn:hover:not(:disabled) { background: #b52228 !important; }
        .fing-upi-qr-frame {
          display: inline-block; padding: 22px; background: #fff;
          border: 2px solid #e8e8e8; border-radius: 20px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.08);
          animation: qrFadeIn 0.4s ease-out;
        }
        .fing-upi-qr-frame img { display: block; width: 320px; height: 320px; }
        @keyframes qrFadeIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }

        .fing-upi-timer-large {
          display: inline-flex; align-items: center; gap: 12px;
          padding: 14px 32px; border-radius: 40px;
          background: linear-gradient(135deg, #fff 0%, ${PRIMARY_LIGHT} 100%);
          border: 2px solid ${PRIMARY_COLOR}30;
          box-shadow: 0 6px 20px rgba(201,38,44,0.08);
          margin: 16px 0 4px 0;
          transition: all 0.3s ease;
        }
        .fing-upi-timer-large .timer-icon { font-size: 32px; color: ${PRIMARY_COLOR}; }
        .fing-upi-timer-large .timer-value {
          font-size: 34px; font-weight: 800; letter-spacing: 1px;
          color: ${PRIMARY_COLOR}; font-variant-numeric: tabular-nums;
        }
        .fing-upi-timer-large.warn {
          background: linear-gradient(135deg, #fff 0%, #fff2f0 100%);
          border-color: #ffccc7;
          animation: warnPulse 1s infinite;
        }
        .fing-upi-timer-large.warn .timer-icon,
        .fing-upi-timer-large.warn .timer-value { color: #cf1322; }
        @keyframes warnPulse {
          0%,100% { box-shadow: 0 6px 20px rgba(207,19,34,0.10); }
          50%     { box-shadow: 0 6px 32px rgba(207,19,34,0.28); }
        }

        .fing-upi-status-overlay {
          position: fixed; inset: 0; z-index: 9999;
          background: rgba(255,255,255,0.94);
          backdrop-filter: blur(6px);
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          gap: 18px; text-align: center; padding: 24px;
          animation: overlayFade 0.25s ease-out;
        }
        @keyframes overlayFade { from { opacity: 0; } to { opacity: 1; } }
        .fing-upi-status-overlay .spin-icon {
          font-size: 72px; color: ${PRIMARY_COLOR};
          animation: spin 1.2s linear infinite;
        }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .fing-upi-status-overlay h3 {
          margin: 0 !important; font-size: 22px !important; font-weight: 700 !important;
          color: #1a1a1a !important;
        }
        .fing-upi-status-overlay p {
          margin: 0 !important; font-size: 14px; color: #666; max-width: 360px;
          line-height: 1.6;
        }
        .fing-upi-status-overlay .progress-dots { display: flex; gap: 8px; margin-top: 6px; }
        .fing-upi-status-overlay .progress-dots span {
          width: 10px; height: 10px; border-radius: 50%;
          background: ${PRIMARY_COLOR}; opacity: 0.25;
          animation: dotPulse 1.4s infinite;
        }
        .fing-upi-status-overlay .progress-dots span:nth-child(2) { animation-delay: 0.2s; }
        .fing-upi-status-overlay .progress-dots span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes dotPulse {
          0%,100% { opacity: 0.25; transform: scale(1); }
          50%     { opacity: 1;    transform: scale(1.15); }
        }

        .fing-upi-warning-card {
          background: #fff7e6;
          border: 1.5px solid #ffd591;
          border-radius: 12px;
          padding: 16px 18px;
          margin-bottom: 16px;
          display: flex;
          gap: 12px;
          align-items: flex-start;
          box-shadow: 0 4px 14px rgba(212, 107, 8, 0.08);
        }
        .fing-upi-tips-card {
          background: #f0f5ff;
          border: 1px solid #adc6ff;
          border-radius: 12px;
          padding: 16px 18px;
          box-shadow: 0 4px 14px rgba(47, 84, 235, 0.06);
        }
      `}</style>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <div className="card rounded-0">
                <div className="card-body">

                  {outsideWindow && (
                    <Alert
                      type="warning"
                      showIcon
                      icon={<WarningOutlined />}
                      style={{ marginBottom: 16, borderRadius: 8 }}
                      message="Service unavailable"
                      description="UPI Cash Withdrawal is restricted between 23:30 and 00:05 as per NPCI guidelines. Please try again after 00:05."
                    />
                  )}

                  <Tabs activeKey={activeTab} onChange={setActiveTab} className="fing-upi-tabs">

                    <TabPane tab={<span><QrcodeOutlined /> Generate QR</span>} key="generate">

                      {/* ---------- STEP 0: FORM ---------- */}
                      {currentStep === 0 && (
                        <div className="row">
                          <div className="col-md-6">
                            <div className="fing-upi-hero">
                              <div>
                                <Title level={3} style={{ color: '#1a1a1a', margin: 0, fontSize: 20 }}>
                                  UPI ATM <span style={{ color: PRIMARY_COLOR, fontWeight: 700 }}>Cash Withdrawal</span>
                                </Title>
                                <Text style={{ color: '#666', fontSize: 13 }}>Scan • Pay • Withdraw Cash Instantly</Text>
                              </div>
                              <div style={{ width: 48, height: 48, borderRadius: '50%', background: PRIMARY_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <QrcodeOutlined style={{ fontSize: 24, color: PRIMARY_COLOR }} />
                              </div>
                            </div>

                            <div className="fing-upi-info">
                              <SafetyOutlined style={{ color: '#666', marginRight: 8 }} />
                              <Text style={{ fontSize: 12, color: '#888' }}>
                                Amount must be a multiple of ₹{AMOUNT_STEP} between ₹{MIN_AMOUNT} and ₹{MAX_AMOUNT}
                              </Text>
                            </div>

                            <Form
                              key={formKey}
                              form={form}
                              layout="vertical"
                              onFinish={handleGenerateQR}
                              size="large"
                              disabled={isFormLocked}
                            >
                              <Form.Item
                                name="mobile"
                                label="Customer's Mobile Number"
                                rules={[{ required: true, message: 'Required' }, { pattern: /^\d{10}$/, message: 'Invalid mobile' }]}
                              >
                                <Input
                                  prefix={<MobileOutlined style={{ color: '#999' }} />}
                                  placeholder="10-digit mobile number"
                                  maxLength={10}
                                  className="fing-upi-input"
                                />
                              </Form.Item>

                              <Form.Item
                                name="amount"
                                label={`Withdrawal Amount (₹${MIN_AMOUNT} - ₹${MAX_AMOUNT})`}
                                rules={[
                                  { required: true, message: 'Required' },
                                  {
                                    validator: (_, v) => {
                                      const n = parseFloat(v);
                                      if (isNaN(n) || n < MIN_AMOUNT) return Promise.reject(`Min ₹${MIN_AMOUNT}`);
                                      if (n > MAX_AMOUNT) return Promise.reject(`Max ₹${MAX_AMOUNT}`);
                                      if (n % AMOUNT_STEP !== 0) return Promise.reject(`Multiple of ₹${AMOUNT_STEP}`);
                                      return Promise.resolve();
                                    }
                                  }
                                ]}
                              >
                                <Input
                                  prefix={<WalletOutlined style={{ color: '#999' }} />}
                                  placeholder={`₹${MIN_AMOUNT} - ₹${MAX_AMOUNT}`}
                                  type="number"
                                  step={AMOUNT_STEP}
                                  className="fing-upi-input"
                                  value={amountInput}
                                  onChange={handleAmountChange}
                                  max={MAX_AMOUNT}
                                />
                              </Form.Item>

                              <div style={{ marginBottom: 16 }}>
                                <Text style={{ fontSize: 12, color: '#999', marginBottom: 8, display: 'block' }}>Quick Amounts:</Text>
                                <div className="fing-upi-quick">
                                  {quickAmounts.map(a => (
                                    <Button
                                      key={a}
                                      size="small"
                                      onClick={() => handleQuickAmount(a)}
                                      className="fing-upi-quick-btn"
                                      disabled={isFormLocked}
                                    >
                                      ₹{a}
                                    </Button>
                                  ))}
                                </div>
                              </div>

                              <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                disabled={isFormLocked}
                                icon={<QrcodeOutlined />}
                                block
                                className="fing-upi-gen-btn"
                              >
                                {qrState === QR_STATE.GENERATING ? 'Generating QR…' : 'Generate QR Code'}
                              </Button>
                            </Form>
                          </div>
                        </div>
                      )}

                      {/* ---------- STEP 1: QR DISPLAY ---------- */}
                      {currentStep === 1 && qrState === QR_STATE.ACTIVE && (
                        <div className="row">

                          {/* LEFT: summary + timer + QR */}
                          <div className="col-md-6" style={{ textAlign: 'center' }}>
                            <div style={{
                              marginBottom: 4, padding: '10px 18px', borderRadius: 10,
                              background: PRIMARY_LIGHT, display: 'inline-block',
                              border: `1px solid ${PRIMARY_COLOR}30`
                            }}>
                              <Text strong style={{ color: PRIMARY_COLOR, fontSize: 16 }}>
                                ₹{sessionSnapshot.amount} &nbsp;•&nbsp; {sessionSnapshot.mobile}
                              </Text>
                            </div>

                            <div className={`fing-upi-timer-large ${timeLeft <= 10 ? 'warn' : ''}`}>
                              <ClockCircleOutlined className="timer-icon" />
                              <span className="timer-value">{formatTime(timeLeft)}</span>
                            </div>

                            {qrData?.intentLink && (
                              <div style={{ marginTop: 12 }}>
                                <div className="fing-upi-qr-frame">
                                  <img
                                    src={`data:image/png;base64,${qrData.intentLink}`}
                                    alt="UPI QR Code"
                                  />
                                </div>
                              </div>
                            )}
                          </div>

                          {/* RIGHT: warning + tips */}
                          <div className="col-md-6">
                            <div className="fing-upi-warning-card">
                              <WarningOutlined style={{ color: '#d46b08', fontSize: 26, flexShrink: 0, marginTop: 2 }} />
                              <div>
                                <Text strong style={{ color: '#d46b08', fontSize: 15, display: 'block', marginBottom: 6 }}>
                                  Payment In Progress
                                </Text>
                                <Text style={{ color: '#874d00', fontSize: 13, lineHeight: 1.8 }}>
                                  Please do not press Back or close or refresh the page, until the payment status is confirmed.
                                  <br />
                                  Interrupting may cause transaction loss.
                                </Text>
                              </div>
                            </div>

                            <div className="fing-upi-tips-card">
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                                <SafetyOutlined style={{ color: '#2f54eb', fontSize: 20, marginRight: 8 }} />
                                <Text strong style={{ color: '#2f54eb', fontSize: 15 }}>How to pay</Text>
                              </div>
                              <ol style={{ margin: 0, paddingLeft: 20, color: '#1d39c4', fontSize: 13, lineHeight: 2 }}>
                                <li>Ask the customer to open any UPI app</li>
                                <li>Scan the QR code shown on the left</li>
                                <li>Verify the amount (<b>₹{sessionSnapshot.amount}</b>) and confirm</li>
                                <li>Wait here — the status will update automatically</li>
                              </ol>
                            </div>

                            {qrData?.qrExpiryNote && (
                              <div style={{
                                marginTop: 16, padding: '10px 14px', borderRadius: 10,
                                background: '#fffbe6', border: '1px solid #ffe58f'
                              }}>
                                <ClockCircleOutlined style={{ color: '#d48806', marginRight: 6 }} />
                                <Text style={{ fontSize: 12, color: '#874d00' }}>
                                  {qrData.qrExpiryNote}
                                </Text>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                    </TabPane>

                    <TabPane tab={<span><HistoryOutlined /> History</span>} key="history">
                      <FingPayUpiCwHistory />
                    </TabPane>
                  </Tabs>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showOverlay && (
        <div className="fing-upi-status-overlay">
          <LoadingOutlined className="spin-icon" />
          <Title level={3}>We're checking your payment status</Title>
          <p>Please wait — do not close this window. We'll take you to the invoice as soon as we get a result.</p>
          <div className="progress-dots"><span /><span /><span /></div>
        </div>
      )}

      <Modal
        title={
          <span>
            <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
            Cancel this QR?
          </span>
        }
        open={abandonModal}
        onOk={performFullReset}
        onCancel={() => setAbandonModal(false)}
        okText="Yes, Generate New"
        okButtonProps={{ danger: true }}
        cancelText="Keep QR"
        maskClosable={false}
      >
        <p style={{ fontSize: 14, lineHeight: 1.6, margin: 0 }}>
          The current QR code for <b>₹{sessionSnapshot.amount}</b> ({sessionSnapshot.mobile}) will be closed.
          <br /><br />
          If the customer has already paid, the transaction may still be processed.
        </p>
      </Modal>
    </div>
  );
};

export default FingPayUpiCw;